import * as ObjectSegFilterTypes from 'customer-data-filters-ui/converters/objectSeg/ObjectSegFilterTypes';
import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import { PRUNING_REFINE_BY, PRUNING_REFINE_BY_TYPE, TIME_COMPARISON, TIME_DIRECTION, TIME_UNIT } from '../constants/constants';
import { NONE, PORTAL_TZ_ALIGNED } from 'customer-data-filters-ui/filterQueryFormat/timestamps/TimestampTypes';
import { convertTimestampToSimpleDate } from 'customer-data-filters-ui/filterQueryFormat/timestamps/TimestampConverters';
import DSFieldRecord from 'customer-data-filters-ui/filterQueryFormat/DSFieldRecord/DSFieldRecord';
import getIn from 'transmute/getIn';
import invariant from 'react-utils/invariant';
import { BACKWARD, FORWARD } from 'customer-data-filters-ui/filterQueryFormat/rollingDates/RollingDateDirections';
import { FQF_TIME_UNITS } from '../../../utils/durationUtilities';
import { DATE } from 'customer-data-objects/property/PropertyTypes';

// These types are based on this doc: https://product.hubteam.com/docs/object-lists/internal_docs/filters/REFINEBYS.html
// We should ultimately use client types for this
const makeDateRefinementField = field => {
  return DSFieldRecord({
    refinement: true,
    type: DATE,
    timestampType: field.get('timestampType')
  });
};
const convertTimestamp = (field, timestamp) => {
  const timestampType = field.get('timestampType');
  if (timestampType === NONE) {
    return convertTimestampToSimpleDate(timestamp, PORTAL_TZ_ALIGNED);
  }
  return convertTimestampToSimpleDate(timestamp, timestampType);
};
const TIME_UNIT_MAPPING = {
  [TIME_UNIT.DAYS]: FQF_TIME_UNITS.days,
  [TIME_UNIT.WEEKS]: FQF_TIME_UNITS.weeks
};
const fromObjectSegTimeUnit = timeUnit => TIME_UNIT_MAPPING[timeUnit];
const fromAbsoluteComparativeTimestampRefineByPruning = (field, filter) => {
  // Ads filters are the only ones that simple comparisons without or equals
  const useSimpleCompare = filter.filterType === ObjectSegFilterTypes.ADS_TIME;
  const comparison = getIn([PRUNING_REFINE_BY, 'comparison'], filter);
  let Operator;
  if (comparison === TIME_COMPARISON.BEFORE) {
    Operator = useSimpleCompare ? Operators.Less : Operators.LessOrEqual;
  } else if (comparison === TIME_COMPARISON.AFTER) {
    Operator = useSimpleCompare ? Operators.Greater : Operators.GreaterOrEqual;
  }
  invariant(Operator, `Unable to parse time refinement for absolute time comparison "${comparison}"`);

  // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
  return Operator.of(makeDateRefinementField(field), convertTimestamp(field, getIn([PRUNING_REFINE_BY, 'timestamp'], filter)));
};
const fromAbsoluteRangedTimestampRefineByPruning = (field, filter) => {
  const rangeType = getIn([PRUNING_REFINE_BY, 'rangeType'], filter);
  let Operator;
  if (rangeType === TIME_COMPARISON.BETWEEN) {
    Operator = Operators.InRange;
  } else if (rangeType === TIME_COMPARISON.NOT_BETWEEN) {
    Operator = Operators.NotInRange;
  }
  invariant(Operator, `Unable to parse time refinement for absolute time range of type "${rangeType}"`);

  // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
  return Operator.of(makeDateRefinementField(field), convertTimestamp(field, getIn([PRUNING_REFINE_BY, 'lowerTimestamp'], filter)), convertTimestamp(field, getIn([PRUNING_REFINE_BY, 'upperTimestamp'], filter)));
};
const fromRelativeComparativeTimestampRefineByPruning = (field, filter) => {
  const comparison = getIn([PRUNING_REFINE_BY, 'comparison'], filter);
  const timeOffset = getIn([PRUNING_REFINE_BY, 'timeOffset'], filter);
  const direction = timeOffset.offsetDirection;
  let Operator;
  let operatorDirection;
  if (comparison === TIME_COMPARISON.AFTER && direction === TIME_DIRECTION.PAST) {
    Operator = Operators.LessThanRolling;
    operatorDirection = 'backward';
  }
  if (comparison === TIME_COMPARISON.BEFORE && direction === TIME_DIRECTION.FUTURE) {
    Operator = Operators.LessThanRolling;
    operatorDirection = 'forward';
  }
  if (comparison === TIME_COMPARISON.BEFORE && direction === TIME_DIRECTION.PAST) {
    Operator = Operators.GreaterThanRolling;
    operatorDirection = 'backward';
  }
  if (comparison === TIME_COMPARISON.AFTER && direction === TIME_DIRECTION.FUTURE) {
    Operator = Operators.GreaterThanRolling;
    operatorDirection = 'forward';
  }
  invariant(Operator, `Unable to parse time refinement for relative time comparison "${comparison}"`);

  // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
  return Operator.of(makeDateRefinementField(field), timeOffset.amount, operatorDirection, fromObjectSegTimeUnit(timeOffset.timeUnit));
};
const fromRelativeRangedTimestampRefineByPruning = (field, filter) => {
  // the operators is LessThanRolling because it is the only operator
  // converted to RelativeRangedTimestampRefineBy
  const Operator = Operators.LessThanRolling;
  const offsetDirection = getIn(['pruningRefineBy', 'lowerBoundOffset', 'offsetDirection'], filter);
  const timeUnit = getIn(['pruningRefineBy', 'lowerBoundOffset', 'timeUnit'], filter);
  let amount;
  let direction;
  if (offsetDirection === TIME_DIRECTION.PAST) {
    direction = BACKWARD;
    amount = getIn(['pruningRefineBy', 'lowerBoundOffset', 'amount'], filter);
  } else {
    direction = FORWARD;
    amount = getIn(['pruningRefineBy', 'upperBoundOffset', 'amount'], filter);
  }
  return Operator.of(makeDateRefinementField(field), amount, direction, fromObjectSegTimeUnit(timeUnit));
};
const FROM_PRUNE_FOR_TYPE = {
  [PRUNING_REFINE_BY_TYPE.ABSOLUTE]: fromAbsoluteComparativeTimestampRefineByPruning,
  [PRUNING_REFINE_BY_TYPE.ABSOLUTE_RANGE]: fromAbsoluteRangedTimestampRefineByPruning,
  [PRUNING_REFINE_BY_TYPE.RELATIVE]: fromRelativeComparativeTimestampRefineByPruning,
  [PRUNING_REFINE_BY_TYPE.RELATIVE_RANGE]: fromRelativeRangedTimestampRefineByPruning
};
const fromDateRefinementFilter = (field, filter) => {
  const pruneType = getIn([PRUNING_REFINE_BY, 'type'], filter);
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
  if (Object.prototype.hasOwnProperty.call(FROM_PRUNE_FOR_TYPE, pruneType)) {
    // @ts-expect-error ts-migrate(2538) FIXME: Type 'unknown' cannot be used as an index type.
    return FROM_PRUNE_FOR_TYPE[pruneType](field, filter);
  }
  return undefined;
};
export default fromDateRefinementFilter;